/**
 * Archer (弓箭手) - Red/orange light armor, smaller helmet, bow with arrow
 * Mobile light infantry with ranged weapons
 * Bow-drawing pose visible from top-down view
 */

import * as THREE from 'three';
import { COLORS } from '../unitConstants';
import { mergeGeometries } from '../unitHelpers';

export function createArcherGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const colors: THREE.Color[] = [];

  // ==================== 头部 ====================
  const head = new THREE.SphereGeometry(0.08, 10, 8);
  head.translate(0, 0.52, 0);
  parts.push(head);
  colors.push(COLORS.skin);

  // ==================== 头盔（轻型） ====================
  // 头盔主体（半球形，比剑士小）
  const helmetDome = new THREE.SphereGeometry(
    0.082,
    8,
    6,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.5
  );
  helmetDome.translate(0, 0.54, 0);
  parts.push(helmetDome);
  colors.push(COLORS.archerRed);

  // 头盔金边
  const helmetRim = new THREE.TorusGeometry(0.078, 0.008, 4, 12);
  helmetRim.rotateX(Math.PI / 2);
  helmetRim.translate(0, 0.54, 0);
  parts.push(helmetRim);
  colors.push(COLORS.gold);

  // ==================== 躯干（轻甲） ====================
  // 上身
  const torso = new THREE.CylinderGeometry(0.065, 0.075, 0.14, 8);
  torso.translate(0, 0.36, 0);
  parts.push(torso);
  colors.push(COLORS.archerRed);

  // 皮质护胸
  const chestGuard = new THREE.BoxGeometry(0.08, 0.06, 0.015);
  chestGuard.translate(0, 0.38, 0.065);
  parts.push(chestGuard);
  colors.push(COLORS.woodDark);

  // 腰带
  const belt = new THREE.CylinderGeometry(0.078, 0.078, 0.02, 8);
  belt.translate(0, 0.27, 0);
  parts.push(belt);
  colors.push(COLORS.gold);

  // 短裙甲（便于移动）
  const skirt = new THREE.CylinderGeometry(0.075, 0.09, 0.07, 8);
  skirt.translate(0, 0.21, 0);
  parts.push(skirt);
  colors.push(COLORS.archerRed);

  // ==================== 箭袋（背在背上，斜向） ====================
  const quiver = new THREE.CylinderGeometry(0.03, 0.035, 0.18, 6);
  quiver.rotateX(0.25);
  quiver.rotateZ(0.35);
  quiver.translate(0.05, 0.36, -0.06);
  parts.push(quiver);
  colors.push(COLORS.woodDark);

  // 箭袋带
  const strap = new THREE.BoxGeometry(0.012, 0.16, 0.006);
  strap.rotateZ(-0.2);
  strap.translate(-0.015, 0.36, 0.04);
  parts.push(strap);
  colors.push(COLORS.wood);

  // 箭袋中的箭头
  for (let i = 0; i < 3; i++) {
    const arrowTip = new THREE.ConeGeometry(0.005, 0.018, 4);
    const angle = (i / 3) * Math.PI * 2;
    arrowTip.translate(
      0.05 + Math.cos(angle) * 0.012,
      0.47,
      -0.06 + Math.sin(angle) * 0.012
    );
    parts.push(arrowTip);
    colors.push(COLORS.steel);
  }

  // ==================== 左臂（向前伸出，握弓） ====================
  // 左上臂 - 从肩膀向前伸
  const leftUpperArm = new THREE.CylinderGeometry(0.022, 0.02, 0.07, 6);
  leftUpperArm.rotateX(-Math.PI / 2.5); // 向前倾斜
  leftUpperArm.rotateZ(0.2); // 略向外
  leftUpperArm.translate(-0.07, 0.40, 0.035);
  parts.push(leftUpperArm);
  colors.push(COLORS.archerRed);

  // 左前臂 - 继续向前
  const leftForearm = new THREE.CylinderGeometry(0.02, 0.016, 0.07, 6);
  leftForearm.rotateX(-Math.PI / 2.5);
  leftForearm.translate(-0.04, 0.38, 0.10);
  parts.push(leftForearm);
  colors.push(COLORS.archerRed);

  // 左手（握弓的位置 - 更靠近角色中心）
  const leftHandX = -0.02;
  const leftHandY = 0.36;
  const leftHandZ = 0.15;
  const leftHand = new THREE.SphereGeometry(0.018, 6, 5);
  leftHand.translate(leftHandX, leftHandY, leftHandZ);
  parts.push(leftHand);
  colors.push(COLORS.skin);

  // ==================== 右臂（向后拉，拉弦） ====================
  // 右上臂 - 从肩膀略向后
  const rightUpperArm = new THREE.CylinderGeometry(0.022, 0.02, 0.07, 6);
  rightUpperArm.rotateX(Math.PI / 3); // 向后倾斜
  rightUpperArm.rotateZ(-0.15);
  rightUpperArm.translate(0.08, 0.40, -0.02);
  parts.push(rightUpperArm);
  colors.push(COLORS.archerRed);

  // 右前臂 - 弯曲向胸前（拉弦位置）
  const rightForearm = new THREE.CylinderGeometry(0.02, 0.016, 0.06, 6);
  rightForearm.rotateX(Math.PI / 4);
  rightForearm.rotateZ(-0.3);
  rightForearm.translate(0.06, 0.36, 0.02);
  parts.push(rightForearm);
  colors.push(COLORS.archerRed);

  // 右手（拉弦的位置，靠近胸口）
  const rightHandX = 0.04;
  const rightHandY = 0.34;
  const rightHandZ = 0.06;
  const rightHand = new THREE.SphereGeometry(0.018, 6, 5);
  rightHand.translate(rightHandX, rightHandY, rightHandZ);
  parts.push(rightHand);
  colors.push(COLORS.skin);

  // ==================== 腿部 ====================
  // 左大腿
  const leftThigh = new THREE.CylinderGeometry(0.028, 0.024, 0.08, 6);
  leftThigh.translate(-0.032, 0.13, 0);
  parts.push(leftThigh);
  colors.push(COLORS.archerRed);

  // 左小腿
  const leftShin = new THREE.CylinderGeometry(0.024, 0.02, 0.07, 6);
  leftShin.translate(-0.032, 0.05, 0);
  parts.push(leftShin);
  colors.push(COLORS.archerRed);

  // 左脚
  const leftFoot = new THREE.BoxGeometry(0.032, 0.015, 0.045);
  leftFoot.translate(-0.032, 0.008, 0.008);
  parts.push(leftFoot);
  colors.push(COLORS.woodDark);

  // 右大腿
  const rightThigh = new THREE.CylinderGeometry(0.028, 0.024, 0.08, 6);
  rightThigh.translate(0.032, 0.13, 0);
  parts.push(rightThigh);
  colors.push(COLORS.archerRed);

  // 右小腿
  const rightShin = new THREE.CylinderGeometry(0.024, 0.02, 0.07, 6);
  rightShin.translate(0.032, 0.05, 0);
  parts.push(rightShin);
  colors.push(COLORS.archerRed);

  // 右脚
  const rightFoot = new THREE.BoxGeometry(0.032, 0.015, 0.045);
  rightFoot.translate(0.032, 0.008, 0.008);
  parts.push(rightFoot);
  colors.push(COLORS.woodDark);

  // ==================== 武器：弓（弓在前方，弧度朝外） ====================
  const bowRadius = 0.12;
  // TorusGeometry默认在XY平面
  // 需要：两端在Y方向（上下），弧度朝Z+方向（前方）
  const bowCurve = new THREE.TorusGeometry(bowRadius, 0.012, 6, 16, Math.PI * 0.85);
  // 1. 先rotateZ使弧竖直（两端在Y方向，弧度在X-方向）
  // 2. 再rotateY使弧度朝前（X-变成Z+）
  bowCurve.rotateZ(Math.PI / 2);
  bowCurve.rotateY(Math.PI / 2);
  bowCurve.translate(leftHandX, leftHandY, leftHandZ);
  parts.push(bowCurve);
  colors.push(COLORS.wood);

  // 弓梢（上下两端）
  const bowTipTop = new THREE.CylinderGeometry(0.005, 0.008, 0.02, 5);
  bowTipTop.translate(leftHandX, leftHandY + bowRadius * 0.9, leftHandZ);
  parts.push(bowTipTop);
  colors.push(COLORS.woodDark);

  const bowTipBottom = new THREE.CylinderGeometry(0.005, 0.008, 0.02, 5);
  bowTipBottom.translate(leftHandX, leftHandY - bowRadius * 0.9, leftHandZ);
  parts.push(bowTipBottom);
  colors.push(COLORS.woodDark);

  // 弓弦（两条直线：从弓顶到右手，从弓底到右手）
  // 弓顶位置
  const bowTopX = leftHandX;
  const bowTopY = leftHandY + bowRadius * 0.85;
  const bowTopZ = leftHandZ;
  // 弓底位置
  const bowBottomX = leftHandX;
  const bowBottomY = leftHandY - bowRadius * 0.85;
  const bowBottomZ = leftHandZ;

  // 弓弦上半段
  const stringTopDx = rightHandX - bowTopX;
  const stringTopDy = rightHandY - bowTopY;
  const stringTopDz = rightHandZ - bowTopZ;
  const stringTopLength = Math.sqrt(stringTopDx * stringTopDx + stringTopDy * stringTopDy + stringTopDz * stringTopDz);
  const bowstringTop = new THREE.CylinderGeometry(0.003, 0.003, stringTopLength, 4);
  // 计算旋转角度
  const stringTopAngleX = Math.atan2(stringTopDz, stringTopLength);
  const stringTopAngleZ = Math.atan2(stringTopDx, -stringTopDy);
  bowstringTop.rotateZ(stringTopAngleZ);
  bowstringTop.rotateX(stringTopAngleX);
  bowstringTop.translate(
    (bowTopX + rightHandX) / 2,
    (bowTopY + rightHandY) / 2,
    (bowTopZ + rightHandZ) / 2
  );
  parts.push(bowstringTop);
  colors.push(new THREE.Color(0xf5f5dc));

  // 弓弦下半段
  const stringBottomDx = rightHandX - bowBottomX;
  const stringBottomDy = rightHandY - bowBottomY;
  const stringBottomDz = rightHandZ - bowBottomZ;
  const stringBottomLength = Math.sqrt(stringBottomDx * stringBottomDx + stringBottomDy * stringBottomDy + stringBottomDz * stringBottomDz);
  const bowstringBottom = new THREE.CylinderGeometry(0.003, 0.003, stringBottomLength, 4);
  const stringBottomAngleX = Math.atan2(stringBottomDz, stringBottomLength);
  const stringBottomAngleZ = Math.atan2(stringBottomDx, -stringBottomDy);
  bowstringBottom.rotateZ(stringBottomAngleZ);
  bowstringBottom.rotateX(stringBottomAngleX);
  bowstringBottom.translate(
    (bowBottomX + rightHandX) / 2,
    (bowBottomY + rightHandY) / 2,
    (bowBottomZ + rightHandZ) / 2
  );
  parts.push(bowstringBottom);
  colors.push(new THREE.Color(0xf5f5dc));

  // ==================== 箭（从右手向外射出） ====================
  // 箭杆：从右手位置向前延伸到弓之外
  const arrowLength = 0.22;
  const arrowShaft = new THREE.CylinderGeometry(0.005, 0.005, arrowLength, 5);
  arrowShaft.rotateX(Math.PI / 2); // 沿Z轴方向
  // 箭从右手向外，箭尾在右手附近，箭头在前方
  arrowShaft.translate(
    0, // X居中
    leftHandY, // Y在手的高度
    rightHandZ + arrowLength / 2 // Z从右手向前延伸
  );
  parts.push(arrowShaft);
  colors.push(COLORS.wood);

  // 箭头（金属，指向前方 Z+）
  const arrowHead = new THREE.ConeGeometry(0.012, 0.035, 4);
  arrowHead.rotateX(Math.PI / 2); // 尖端朝向 Z+ 方向
  arrowHead.translate(
    0,
    leftHandY,
    rightHandZ + arrowLength + 0.015 // 在箭杆前端
  );
  parts.push(arrowHead);
  colors.push(COLORS.steel);

  // 箭羽（红色，在箭尾，靠近右手）
  for (let i = 0; i < 3; i++) {
    const fletch = new THREE.BoxGeometry(0.018, 0.022, 0.003);
    const fletchAngle = (i / 3) * Math.PI * 2;
    fletch.rotateZ(fletchAngle);
    fletch.translate(
      Math.cos(fletchAngle) * 0.01,
      leftHandY + Math.sin(fletchAngle) * 0.01,
      rightHandZ + 0.02 // 在箭尾，靠近右手
    );
    parts.push(fletch);
    colors.push(COLORS.tassel);
  }

  return mergeGeometries(parts, colors);
}
